import { ComponentProps, useEffect, useMemo } from 'react';
import { ListBoxItem } from '@heroui/react';

import {
  ModelCreateModal,
  FieldSelect,
  FieldInput
} from '@rhino-project/ui-heroui';
import { useModelShow } from '@rhino-project/core/hooks';
import { useFormContext, useWatch } from 'react-hook-form';
import { RhinoPathEntries } from '@rhino-project/core';

const ratingOptions = Array.from({ length: 10 }, (_x, i) => {
  const val = ((i + 1) / 2).toFixed(1);
  return { id: val, display_name: val };
});

interface ReportFieldData {
  id: string | number;
  name: string;
  field_type: string;
}

interface ReportFieldProps {
  field: ReportFieldData;
  idx: number;
  variant?: string;
}

const ReportField = ({ field, idx, variant }: ReportFieldProps) => {
  const path = useMemo(() => `report_values.${idx}.value`, [idx]);

  switch (field.field_type) {
    case 'text':
      return <FieldInput label={field.name} path={path} variant={variant} />;
    default:
      return (
        <FieldSelect label={field.name} path={path} variant={variant}>
          {ratingOptions.map((opt) => (
            <ListBoxItem key={opt.id} id={opt.id} textValue={opt.display_name}>
              {opt.display_name}
            </ListBoxItem>
          ))}
        </FieldSelect>
      );
  }
};

interface ReportType {
  report_fields?: ReportFieldData[];
}

const ReportForm = ({
  variant
}: {
  variant?: string;
  [key: string]: unknown;
}) => {
  const { setValue } = useFormContext();
  // v3 reference fields store the identifier directly, not as { id: ... }
  const reportId = useWatch({ name: 'report_type' });

  const { resource: reportType } = useModelShow('report_type', reportId, {
    queryOptions: { enabled: !!reportId }
  });

  const renderFields = useMemo(() => {
    return (reportType as ReportType | null)?.report_fields?.map(
      (field, idx) => {
        return (
          <ReportField
            key={`${field.id}`}
            field={field}
            idx={idx}
            variant={variant}
          />
        );
      }
    );
  }, [reportType, variant]);

  useEffect(() => {
    const defaultValues = (reportType as ReportType | null)?.report_fields?.map(
      (field) => ({
        report_field: field.id,
        // FIXME: This could be a little more nuanced
        value: field.field_type === 'text' ? '' : -1
      })
    );

    setValue('report_values', defaultValues);
  }, [setValue, reportType]);

  return <div>{renderFields}</div>;
};

interface ReportSidebarFormProps extends ComponentProps<
  typeof ModelCreateModal
> {
  parentId?: string | number | null;
}

const ReportSidebarForm = (props: ReportSidebarFormProps) => {
  const { parentId } = props;

  const paths: RhinoPathEntries<'report'> = useMemo(() => {
    return [
      // String paths so these required fields are client-validated. reported_at
      // is first, so the modal's default autofocus (setFocus on the first
      // string path) lands on it.
      'reported_at',
      'observing',
      'venue',
      'report_type',
      // eslint-disable-next-line @eslint-react/no-missing-key
      <ReportForm />
    ];
  }, []);

  if (!parentId) return null;

  return (
    <ModelCreateModal
      {...props}
      model="report"
      paths={paths}
      extraDefaultValues={
        // eslint-disable-next-line @eslint-react/purity -- Preserve existing behavior while enabling the rule globally.
        { reported_at: new Date().toISOString() }
      }
    />
  );
};

export default ReportSidebarForm;
